import {
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { Star } from "lucide-react";
import Form, { IFormProps, withForm } from "@ui/components/forms";
import { useFormContext } from "react-hook-form";
import { TextAreaField } from "@ui/components/document/fields/TextFields";
import { IFeedback, useFeedback } from "@/services/useFeedBack";
import { getMealIcon } from "./GuestBookingDetailsForm";
import { useEffect, useState, useMemo } from "react";
import { z } from "zod";
import { orderBy } from "lodash";

type T = IFeedback;
const Field = Form.Field<any>;

interface IProps {
  mealServedData?: any[];
  adhocMealServedData?: any[];
  isLoading: boolean;
}

export const schema = z.object({
  rating: z
    .number({
      required_error: "Rating is required",
      invalid_type_error: "Rating is required",
    })
    .min(1, "Minimum rating is 1")
    .max(5, "Maximum rating is 5"),
  comment: z
    .string({
      message: "Comment is mandatory",
      required_error: "Comment is mandatory",
    })
    .min(1, "Comment is mandatory"),
});

const FeedbackForm = ({
  Wrapper,
  onClose,
  mealServedData = [],
  adhocMealServedData = [],
  isOpen,
  isLoading,
}: IFormProps & IProps) => {
  const { handleSubmit, watch, setValue, reset } = useFormContext<T>();
  const { bulkAdd } = useFeedback(undefined);

  const [selectedMealId, setSelectedMealId] = useState<string | null>(null);
  const [selectedAdhocMealId, setSelectedAdhocMealId] = useState<string | null>(
    null,
  );

  const [feedbackCache, setFeedbackCache] = useState<
    Record<string, Partial<IFeedback>>
  >({});

  useEffect(() => {
    if (isOpen) {
      setSelectedMealId(null);
      setSelectedAdhocMealId(null);
      reset({ rating: 0, comment: "" });
      setFeedbackCache({});
    }
  }, [isOpen, reset]);

  const rating = watch("rating");
  const comment = watch("comment");

  const selectedMeal = mealServedData.find(
    (meal) => meal.id === selectedMealId,
  );
  const selectedAdhocMeal = adhocMealServedData.find(
    (meal) => meal.id === selectedAdhocMealId,
  );

  const hasSubmitted = (mealId: string) =>
    !!mealServedData.find((m) => m.id === mealId)?.feedback?.id;
  const hasAdhocSubmitted = (mealId: string) =>
    !!adhocMealServedData.find((m) => m.id === mealId)?.feedback?.id;

  const cacheCurrent = () => {
    if (selectedMealId) {
      setFeedbackCache((prev) => ({
        ...prev,
        [selectedMealId]: { rating, comment },
      }));
    } else if (selectedAdhocMealId) {
      setFeedbackCache((prev) => ({
        ...prev,
        [`adhoc:${selectedAdhocMealId}`]: { rating, comment },
      }));
    }
  };

  const handleFeedbackSelection = (mealId: string, isAdhoc: boolean) => {
    cacheCurrent();

    const cacheKey = isAdhoc ? `adhoc:${mealId}` : mealId;
    const feedback = isAdhoc
      ? adhocMealServedData.find((m) => m.id === mealId)?.feedback
      : mealServedData.find((m) => m.id === mealId)?.feedback;
    const cached = feedbackCache[cacheKey];

    reset({
      rating: cached?.rating ?? feedback?.rating ?? 0,
      comment: cached?.comment ?? feedback?.comment ?? "",
    });

    setSelectedMealId(isAdhoc ? null : mealId);
    setSelectedAdhocMealId(isAdhoc ? mealId : null);
  };

  const handleUpdate = () => {
    const allFeedbacks = {
      ...feedbackCache,
      ...(selectedMealId && { [selectedMealId]: { rating, comment } }),
      ...(selectedAdhocMealId && {
        [`adhoc:${selectedAdhocMealId}`]: { rating, comment },
      }),
    };

    const feedbacksToSubmit = Object.entries(allFeedbacks)
      .filter(([mealId, fb]) => {
        if (mealId.startsWith("adhoc:")) {
          const id = mealId.replace("adhoc:", "");
          return !hasAdhocSubmitted(id) && fb.rating;
        } else {
          return !hasSubmitted(mealId) && fb.rating;
        }
      })
      .map(([mealId, fb]) => {
        const isAdhoc = mealId.startsWith("adhoc:");
        const id = mealId.replace("adhoc:", "");
        return {
          rating: fb.rating!,
          comment: fb.comment ?? "",
          mealServingId: isAdhoc ? undefined : id,
          adhocServingId: isAdhoc ? id : undefined,
        };
      });

    if (feedbacksToSubmit.length > 0) {
      bulkAdd.mutate(feedbacksToSubmit, { onSuccess: onClose });
    }
  };

  const sortedMeals = useMemo(() => {
    return orderBy(
      [...mealServedData, ...adhocMealServedData],
      [
        (meal) =>
          new Date("requestedAt" in meal ? meal.requestedAt : meal.servedAt),
      ],
      ["asc"],
    );
  }, [mealServedData, adhocMealServedData]);

  const footer = (direction: "rtl" | "ltr") => (
    <Form.Footer direction={direction}>
      <Button
        isLoading={bulkAdd.isPending}
        onClick={handleSubmit(handleUpdate)}
        disabled={
          (!selectedMealId && !selectedAdhocMealId) ||
          (mealServedData.every((m) => m.feedback?.id) &&
            adhocMealServedData.every((m) => m.feedback?.id))
        }
      >
        Submit
      </Button>
    </Form.Footer>
  );

  return (
    <Wrapper title="Feedback" footer={footer}>
      {isLoading ? (
        <Box mt={6} textAlign="center">
          <Spinner size="lg" color="green.500" thickness="4px" />
          <Text mt={2} fontSize="sm" color="gray.500">
            Loading your feedbacks...
          </Text>
        </Box>
      ) : mealServedData.length === 0 && adhocMealServedData.length === 0 ? (
        <Text>No meals have been served recently.</Text>
      ) : (
        <>
          {sortedMeals.map((meal) => {
            const isAdhoc = "requestedAt" in meal;
            const mealId = meal.id;
            const IconComponent = getMealIcon(
              isAdhoc ? meal.meal : meal.mealBooking.meal,
            );
            const isSelected = isAdhoc
              ? selectedAdhocMealId === mealId
              : selectedMealId === mealId;
            const isSubmitted = isAdhoc
              ? hasAdhocSubmitted(mealId)
              : hasSubmitted(mealId);

            return (
              <Box
                key={mealId}
                as="button"
                cursor="pointer"
                borderWidth="1px"
                borderRadius="md"
                bg={
                  isSelected
                    ? isSubmitted
                      ? "blue.100"
                      : "green.100"
                    : "white"
                }
                borderColor={
                  isSelected
                    ? isSubmitted
                      ? "blue.400"
                      : "green.400"
                    : "gray.200"
                }
                boxShadow={isSelected ? "md" : "sm"}
                px={4}
                py={3}
                minW="full"
                textAlign="left"
                transition="all 0.2s"
                onClick={() => handleFeedbackSelection(mealId, isAdhoc)}
                mb={2}
              >
                <Flex align="center" justify="space-between">
                  <Box display="flex" alignItems="center" gap={3}>
                    {IconComponent && (
                      <Box as={IconComponent} boxSize={5} color="gray.700" />
                    )}
                    <Text fontWeight="medium">
                      {(isAdhoc ? meal.meal : meal.mealBooking.meal).replace(
                        /^./,
                        (c: any) => c.toUpperCase(),
                      )}
                    </Text>
                  </Box>
                  <Text fontSize="sm" color="gray.600">
                    {new Date(
                      isAdhoc ? meal.requestedAt : meal.mealBooking.date,
                    ).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                  </Text>
                </Flex>
              </Box>
            );
          })}
          <Form<T>>
            {selectedMeal?.id || selectedAdhocMeal?.id ? (
              <>
                <Field name="rating" label="Rate" isRequired>
                  {() => {
                    const isSubmitted = selectedMeal
                      ? hasSubmitted(selectedMeal.id)
                      : selectedAdhocMeal
                        ? hasAdhocSubmitted(selectedAdhocMeal.id)
                        : false;

                    return (
                      <HStack spacing={1}>
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Icon
                            key={star}
                            as={Star}
                            cursor={isSubmitted ? "default" : "pointer"}
                            color={star <= rating ? "yellow.400" : "gray.300"}
                            boxSize={6}
                            onClick={() =>
                              !isSubmitted && setValue("rating", star)
                            }
                            _hover={
                              !isSubmitted ? { color: "yellow.300" } : undefined
                            }
                          />
                        ))}
                      </HStack>
                    );
                  }}
                </Field>
                <TextAreaField
                  field={{
                    name: "comment",
                    label: "Comment",
                    type: "textarea",
                  }}
                  isEditable={
                    !(selectedMeal
                      ? hasSubmitted(selectedMeal.id)
                      : selectedAdhocMeal
                        ? hasAdhocSubmitted(selectedAdhocMeal.id)
                        : true)
                  }
                  isRequired
                />
              </>
            ) : (
              <Text mt={4} color="gray.600">
                Select a meal to provide feedback.
              </Text>
            )}
          </Form>
        </>
      )}
    </Wrapper>
  );
};

const FeedbackFormHOC = withForm<any, any>(FeedbackForm, schema);
export default FeedbackFormHOC;
